/**
 * The shutter: the one screen effect a session needs that cannot be a screen
 * effect at all.
 *
 * `render/postfx.ts` already carries the comfort tunnel in a headset — a
 * blend-only overlay drawn per eye, with a `uComfort` term this file's
 * locomotion drives. That is the right home for it: it is one triangle the
 * frame is already paying for. So this is deliberately *not* a second vignette.
 *
 * What it is for is the two things a blend overlay cannot do:
 *
 *  - **Full black.** The overlay's comfort term is a surround; it never closes
 *    the centre. A head pushed into a wall, and the blink over a snap turn,
 *    both need the picture to go away entirely.
 *  - **A tunnel that does not depend on a graphics setting.** Comfort is not a
 *    look, it is the difference between a child playing tomorrow and not, so it
 *    must not switch off with post-processing. When the grade is off — by
 *    setting or because the frame-rate governor gave it up — the tunnel falls
 *    back to here.
 *
 * The mesh is a unit sphere parented to the camera, drawn last, with depth
 * testing off. Alpha comes from the angle between the fragment and straight
 * ahead in *object* space, so both eyes see the surround at the same angular
 * position and there is nothing to try to fuse. It is hidden outright whenever
 * it has nothing to say, which is almost every frame — an alpha-blended
 * fullscreen fill on a tile-based mobile GPU is not something to leave running.
 */

import * as THREE from 'three';
import { clamp01, damp } from '../core/math';

/**
 * Half-angle of the clear centre with the tunnel fully open, radians.
 *
 * Past the corner of either eye's view on every headset going, so "open" is
 * genuinely invisible rather than a faint ring somebody has to look for.
 */
const OPEN_ANGLE = 1.75;

/** Half-angle of the clear centre at full aperture, radians. About 30°. */
const TIGHT_ANGLE = 0.53;

/** Width of the soft edge, radians. A hard edge reads as a mask, not as vision. */
const FEATHER = 0.3;

/** How fast the aperture follows its target, 1/s. It must never snap. */
const APERTURE_RATE = 6;

/**
 * How fast the blackout follows its target, 1/s. Asymmetric, deliberately.
 *
 * A shutter has one job: be **shut before the thing it is hiding happens**. A
 * snap turn and a teleport hop are both instants, and both ask for a blink of
 * well under a tenth of a second — at a single symmetric rate of 14 a 75 ms
 * pulse only ever reaches about half of full black on screen, so the player
 * sees the discontinuity through a grey veil, which is the worst of both. It
 * also silently caps how short a blink the rest of the code is allowed to ask
 * for, which is a policy decision hiding in a smoothing constant.
 *
 * So closing is near-immediate — 45 is most of the way there in two frames at
 * 72 Hz — and opening keeps the gentler rate, because coming *back* is the part
 * the eye is actually watching and a hard cut back to full brightness reads as
 * a flash. Neither is a snap: even the close is smoothed, because a single
 * fully black frame appearing from nowhere is its own artefact in stereo.
 */
const BLACK_CLOSE_RATE = 45;
const BLACK_OPEN_RATE = 14;

const VERTEX = /* glsl */ `
varying vec3 vDir;

void main() {
  // Object space, so the angle is measured from the head's forward axis and is
  // identical in both eyes. Using the view vector instead would give each eye a
  // slightly different ring and something to try to fuse.
  vDir = normalize(position);
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const FRAGMENT = /* glsl */ `
uniform float uCosInner;
uniform float uCosOuter;
uniform float uBlack;

varying vec3 vDir;

void main() {
  // -Z is forward, so this is 1 straight ahead and -1 directly behind.
  float c = -vDir.z;
  float a = 1.0 - smoothstep(uCosOuter, uCosInner, c);
  a = max(a, uBlack);
  if (a <= 0.004) discard;
  gl_FragColor = vec4(0.0, 0.0, 0.0, a);
}
`;

/** The shutter. One instance lives for the length of a session. */
export interface ComfortVignette {
  /** Parent this to the camera. */
  readonly object: THREE.Object3D;
  /**
   * 0 = wide open, 1 = a narrow tunnel. Only used when the post overlay's own
   * comfort term is unavailable; see the file header. Damped internally, so a
   * caller may write a step function into it every frame without anybody
   * feeling it.
   */
  setAperture(v: number): void;
  /**
   * 0..1 hard blackout: a head pushed into a wall, the blink on a snap turn.
   * Also damped, but much faster — this one really is a shutter.
   */
  setBlackout(v: number): void;
  update(dt: number): void;
  dispose(): void;
}

/**
 * Build the tunnel. Nothing is added to any scene: the caller parents
 * `object` to the camera when a session starts and removes it when one ends,
 * so a flat game never owns the geometry at all.
 */
export function makeComfortVignette(): ComfortVignette {
  const uniforms = {
    uCosInner: { value: Math.cos(OPEN_ANGLE) },
    uCosOuter: { value: Math.cos(OPEN_ANGLE + FEATHER) },
    uBlack: { value: 0 },
  };

  const material = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: VERTEX,
    fragmentShader: FRAGMENT,
    transparent: true,
    depthTest: false,
    depthWrite: false,
    // The camera sits inside it, so only the inside faces are ever wanted.
    side: THREE.BackSide,
    fog: false,
    toneMapped: false,
  });

  // Radius 1 m: well inside the near plane of nothing and well outside both
  // eyes, so the ~3 cm interpupillary offset is a rounding error on the angle.
  const geometry = new THREE.SphereGeometry(1, 24, 16);
  const mesh = new THREE.Mesh(geometry, material);
  mesh.frustumCulled = false;
  // Last, over everything, including the entity views that draw with their own
  // render orders.
  mesh.renderOrder = 10000;
  mesh.matrixAutoUpdate = false;
  mesh.updateMatrix();

  let aperture = 0;
  let apertureTarget = 0;
  let black = 0;
  let blackTarget = 0;

  return {
    object: mesh,

    setAperture(v: number): void {
      apertureTarget = clamp01(v);
    },

    setBlackout(v: number): void {
      blackTarget = clamp01(v);
    },

    update(dt: number): void {
      aperture = damp(aperture, apertureTarget, APERTURE_RATE, dt);
      black = damp(black, blackTarget, blackTarget > black ? BLACK_CLOSE_RATE : BLACK_OPEN_RATE, dt);
      if (aperture < 0.002) aperture = 0;
      if (black < 0.002) black = 0;

      // Nothing to say, so nothing is drawn. This matters more than it looks:
      // the mesh is a fullscreen alpha-blended fill, and leaving one running
      // every frame on a mobile tiler for the sake of a `discard` is exactly
      // the sort of thing that costs a headset its frame rate.
      mesh.visible = aperture > 0 || black > 0;
      if (!mesh.visible) return;

      const inner = OPEN_ANGLE + (TIGHT_ANGLE - OPEN_ANGLE) * aperture;
      uniforms.uCosInner.value = Math.cos(inner);
      uniforms.uCosOuter.value = Math.cos(Math.min(Math.PI, inner + FEATHER));
      uniforms.uBlack.value = black;
    },

    dispose(): void {
      mesh.parent?.remove(mesh);
      geometry.dispose();
      material.dispose();
    },
  };
}
